<template>
  <div>
    <v-table
      v-if="showActiveVulnerabilities"
      density="compact"
      data-testid="active-vulnerabilities"
      hover
    >
      <thead>
        <tr>
          <th>
            <span class="subtitle2">{{ $t("general.topPriority") }}</span>
          </th>
          <th class="text-center">
            <a class="body2 coro-link" @click="onAllActiveClick()">{{ $t("general.all") }}</a>
          </th>
          <th :aria-label="$t('ariaLabels.actionsColumn')"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="vulnerability in sortedVulnerabilities" :key="vulnerability.issue">
          <td class="text-left vulnerability-col">
            <div class="text-no-wrap d-flex align-center">
              <div class="inner-circle mr-2 coro-widget--active-vulnerability-circle"></div>
              <a
                class="coro-link body2 vulnerability-text"
                :data-testid="`widget-${kebabCase(vulnerability.issue)}-link`"
                @click="onViewDetailsClick(vulnerability, true)"
                :title="$t(`tickets.eventTriggers.${vulnerability.issue}`)"
              >
                <slot name="active-vulnerability" v-bind="vulnerability">
                  {{ $t(`tickets.eventTriggers.${vulnerability.issue}`) }}
                </slot>
              </a>
            </div>
          </td>
          <td
            class="text-center number-col subtitle2 text-red-dark"
            :data-testid="`widget-${kebabCase(vulnerability.issue)}-count`"
          >
            {{ vulnerability.count }}
          </td>
          <td class="text-right action-col">
            <v-btn
              :data-testid="`widget-${kebabCase(vulnerability.issue)}-resolve-btn`"
              rounded
              size="x-small"
              color="primary"
              @click="onViewDetailsClick(vulnerability, true)"
            >
              {{ $t("general.review") }}
            </v-btn>
          </td>
        </tr>
      </tbody>
    </v-table>
    <div class="d-flex align-center h-100 justify-center flex-column" v-else>
      <v-icon icon="$successSmall" size="80" />
      <div class="headline6">{{ $t("general.allCaughtUp") }}</div>
      <div class="coro-link mt-4" @click="onAllCaughtUpClick()">{{ $t("general.viewEvents") }}</div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, ref, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import kebabCase from "lodash/kebabCase";
import { trackGtmEvent } from "@/_helpers/analytics";
import { GtmAction, GTMParams } from "@/constants/gtm.js";
import type { WidgetData, WidgetVulnerability } from "@/_store/dashboard/dashboard.module.ts";

export default {
  props: {
    widgetData: {
      type: Object as PropType<WidgetData>,
      required: true,
    },
    displayShowMoreButton: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["all-active-link-clicked", "all-caught-up-link-clicked", "vulnerability-clicked"],
  setup(props, { emit }) {
    const showAllResolved = ref(false);
    const { t } = useI18n();

    const showActiveVulnerabilities = computed(() => {
      return props.widgetData.activeVulnerabilities.length > 0;
    });

    const sortedVulnerabilities = computed(() => {
      if (!props.widgetData.activeVulnerabilities?.length) {
        return [];
      }
      return props.widgetData.activeVulnerabilities.slice().sort((a, b) => {
        // Sort by count first, then use localized labels
        const countDiff = b.count - a.count;
        if (countDiff !== 0) {
          return countDiff;
        }
        return t(`tickets.eventTriggers.${a.issue}`).localeCompare(
          t(`tickets.eventTriggers.${b.issue}`)
        );
      });
    });

    const onAllActiveClick = () => {
      emit("all-active-link-clicked");
    };

    const onAllCaughtUpClick = () => {
      emit("all-caught-up-link-clicked");
    };

    const onViewDetailsClick = (vulnerability: WidgetVulnerability, isActive: boolean) => {
      if (!vulnerability.count) return;
      emit("vulnerability-clicked", {
        isActive,
        vulnerability,
      });
    };

    const onDisplayMoreClick = (show: boolean) => {
      showAllResolved.value = show;
      const action = show
        ? GTMParams.SHOW_MORE_VULNERABILITIES
        : GTMParams.SHOW_LESS_VULNERABILITIES;
      trackGtmEvent(GtmAction.WORKSPACE_VIEW_DASHBOARD_WIDGET, action);
    };

    return {
      kebabCase,
      showAllResolved,
      showActiveVulnerabilities,
      sortedVulnerabilities,
      onAllActiveClick,
      onAllCaughtUpClick,
      onViewDetailsClick,
      onDisplayMoreClick,
    };
  },
};
</script>

<style scoped lang="scss">
.action-col {
  max-width: 90px;
}

.number-col {
  max-width: 40px;
}

.vulnerability-col {
  max-width: 290px;
  min-width: 230px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.v-table {
  box-shadow: unset !important;
}

:deep(*) {
  .v-table td a {
    color: rgb(var(--v-theme-primary)) !important;
  }
}

.vulnerability-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 290px;
  display: inline-block;
}
</style>
